/** biome-ignore-all lint/a11y/noStaticElementInteractions: Temporarily disabled, click handler on div tag */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: Temporarily disabled, click handler on div tag */
import type { CatalogItemPreview, MemberDataFirestoreRecord } from "@albertine/types";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAgents } from "../../common/hooks/useAgents";
import type { Agent } from "../../common/types/Agent";
import {
    type Booking,
    type BookingInMessage,
    parseBookingToMessage,
} from "../../common/types/Booking";
import type { Conversation } from "../../common/types/Conversation";
import type { FirebaseTimestamp } from "../../common/types/FirebaseTimestamp";
import { InternalNoteWithMention, type Message } from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import { excludeUndefined } from "../../common/utils/array.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { EventEmitter, type EventListener } from "../../EventEmitter";
import "../../css/Tooltip.css";
import { isAppVersionLower } from "../../utils/app.util";
import { mapApiBookingToBooking } from "../../utils/booking.adapter";
import { formatChatMessageDate } from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import type {
    MessageInputContent,
    MessageInputFunction,
    MessageState,
    MessageType,
} from "./ChatContext";
import Mentions from "./Mentions";
import MessageAttachmentPreviews from "./MessageAttachmentPreviews";
import "./MessageInput.css";
import { useGet, usePost } from "@albertine/api-client";
import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { Info } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Badge } from "@/components/ui/badge";
import { createMembershipTileState } from "@/utils/members.util";
import { mapArticlesToDeepLink } from "../../common/types/Storyblok";
import { logErrorAndShowToast } from "../../utils/window.util";
import { ExpiredMemberBadge } from "../Membership/ExpiredMemberBadge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { formatReplyPlaceHolderText, formatReplyToText } from "./chat.util";
import QuillEditor, { type QuillEditorActions } from "./QuillEditor";

const MESSAGE_MAX_CHARACTER_COUNT = 500;
const MESSAGE_OVER_LIMIT_TOOLTIP_TEXT =
    "Long messages are harder to read on mobile. Consider making your message more concise and leading with the most important details.";

type Props = {
    action: MessageInputFunction;
    editedMessageTimestamp?: FirebaseTimestamp | null | undefined;
    messageBeingEdited?: Message | undefined;
    request: Request | undefined;
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    messageState: MessageState;
    sendAction: (content: MessageInputContent) => void;
    attachAction?: () => void;
    closeAction?: () => void;
    conversation?: Conversation;
};

function MessageInput({
    action,
    editedMessageTimestamp,
    messageBeingEdited,
    member,
    request,
    currentAgent,
    messageState,
    sendAction,
    attachAction,
    closeAction = () => {},
    conversation,
}: Props) {
    const {
        messageType,
        setMessageType,
        replyText,
        setReplyText,
        replyHtml,
        setReplyHtml,
        bookings,
        removeBooking,
        attachments,
        addFilesToAttachments,
        removeAttachment,
        articles,
        removeArticle,
        articlesV2,
        removeArticleV2,
        clearChatContext,
    } = messageState;
    const [isSending, setIsSending] = useState(false);
    const [linkEditorActive, setLinkEditorActive] = useState(false);

    // AI Summary state
    const [showAiSummary, setShowAiSummary] = useState(false);
    const { mutateAsync: generateMemberSummary, isPending: isGeneratingAiSummary } =
        usePost("generateMemberSummary");

    // PostHog feature flag for AI functionality
    const aiSummaryButtonEnabled =
        useFeatureFlagEnabled("ai_summary_button_enabled") ?? false;

    const editorRef = useRef<QuillEditorActions>(null);

    useEffect(() => {
        const addMessageToChatReply: EventListener = {
            name: "addMessageToChatReply",
            listener: (params) => {
                editorRef.current?.setMessage({
                    message: params.message,
                } as Message);
            },
        };
        EventEmitter.addListener(addMessageToChatReply);
        return () => {
            EventEmitter.removeListener(addMessageToChatReply);
        };
    }, []);

    const agents = useAgents();
    const [mentionedAgents, setMentionedAgents] = useState<Agent[]>([]);
    const [dueDate, setDueDate] = useState<Date | undefined>(undefined);

    const [bookingsInMessage, setBookingsInMessage] = useState<BookingInMessage[]>([]);
    const { data: onboarding } = useGet(
        "memberOnboardingPreferences",
        { param: { memberId: member.id } },
        { enabled: !!member.id },
    );

    const bookingIds = bookings && bookings.length > 0 ? bookings.map((b) => b.id) : [];

    const { data: fetchedBookingsData } = useGet(
        "bookings",
        { query: { bookingIds, archived: "true" } },
        { enabled: bookingIds.length > 0 && action !== "edit" },
    );

    useEffect(() => {
        if (!bookings || bookings.length === 0) {
            setBookingsInMessage([]);
            return;
        }

        if (action === "edit") {
            setBookingsInMessage(bookings);
            return;
        }

        if (fetchedBookingsData?.bookings) {
            const ids = bookings.map((b) => b.id);
            const parsed = fetchedBookingsData.bookings
                .map((b) => parseBookingToMessage(mapApiBookingToBooking(b)))
                .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
            setBookingsInMessage(parsed);
        }
    }, [bookings, fetchedBookingsData, action]);

    const hasMessageContent = () =>
        !!replyText?.trim() ||
        (attachments && attachments.length > 0) ||
        (bookings && bookings.length > 0);
    const uploadAttachmentsRef = useRef<HTMLInputElement>(null);
    const activeButtonRef = useRef<HTMLButtonElement>(null);
    const activeSecondaryButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (messageBeingEdited && editorRef.current) {
            if (isTypeOf(messageBeingEdited, InternalNoteWithMention)) {
                setMentionedAgents(
                    messageBeingEdited.mentionedAgents
                        .map((mentionedAgentId) =>
                            agents.find((agent) => agent.id === mentionedAgentId),
                        )
                        .filter(excludeUndefined),
                );
                setDueDate(
                    messageBeingEdited.mentionDueDate
                        ? fromTimestampToDate(messageBeingEdited.mentionDueDate)
                        : undefined,
                );
            }
        }
    }, [agents, messageBeingEdited]);

    const clearInput = () => {
        setReplyText("");
        setReplyHtml("");
        clearChatContext();
        setMentionedAgents([]);
        setDueDate(undefined);
        editorRef.current?.clear();
    };

    const handleGenerateAiSummary = async () => {
        if (!conversation || !currentAgent || isSending || isGeneratingAiSummary) {
            return;
        }

        try {
            await generateMemberSummary({
                param: {
                    memberId: member.id,
                    conversationId: conversation.id,
                },
                json: request?.id ? { requestId: request.id } : {},
            });

            EventEmitter.emit({ name: "collapseOldAiSummaries", params: {} });
        } catch (error) {
            logErrorAndShowToast(error, "AI Summary generation failed");
        }
    };

    const sendMessage = async () => {
        if (!currentAgent) return;
        if (!hasMessageContent()) return;
        if (isSending) return;

        setIsSending(true);
        try {
            await sendAction({
                replyText,
                replyHtml,
                bookings: bookingsInMessage,
                attachments,
                messageType,
                mentionedAgents,
                articles,
                articlesV2,
                dueDate,
            });
        } catch (error) {
            alert(error);
            setIsSending(false);
            return;
        }

        clearInput();
        setIsSending(false);
    };

    const tabSelectionVisible = action === "send";

    // Required checks for app version lower than 1.2.7 to
    // compose message with hyperlinks to the shared WOA articles (backward compatibility)
    const shouldManageArticleHyperlinks =
        member.appVersion && isAppVersionLower(member.appVersion, "1.2.7");

    useEffect(() => {
        if (articles && articles.length > 0) {
            const deepLinks = mapArticlesToDeepLink(articles);

            if (shouldManageArticleHyperlinks) {
                deepLinks.forEach((item) => {
                    // Note: search for a unique slug deep link is safer than search for a name
                    // because Html usually transform special chars in the name to unicode
                    if (!replyHtml?.includes(item.slugDeepLink)) {
                        editorRef.current?.createLink(item.name, item.slugDeepLink);
                    }
                });
            }
        }
    }, [articles]);

    const placeHolderText = useMemo(
        () => formatReplyPlaceHolderText(messageType, onboarding),
        [onboarding, messageType],
    );

    const replyTabText = useMemo(
        () => formatReplyToText(request, member, onboarding),
        [onboarding, request, member],
    );

    const { data: membershipTiers = [] } = useGet("membershipTiers", {});
    const membershipState = createMembershipTileState(
        member.membership,
        membershipTiers,
        new Date(),
    );

    return (
        <div
            className={classNames(
                "chat-reply",
                { "chat-reply__is-editing": action === "edit" },
                {
                    "chat-reply--internal-note": messageType === "internal_note",
                },
            )}
        >
            {membershipState.expired && (
                <ExpiredMemberBadge variant="membershipExpired" />
            )}

            <div className="chat-reply__container">
                {isSending && action === "send" && (
                    <div className="chat-reply__is-sending">
                        <div className="flex items-center gap-2">
                            <LoadingSpinner />
                            Sending...
                        </div>
                    </div>
                )}
                {action === "edit" && (
                    <div className="chat-reply__edit-info">
                        <Label.Medium.N50>
                            Edit{" "}
                            {messageType === "internal_note"
                                ? "internal note"
                                : "message"}{" "}
                            from {formatChatMessageDate(editedMessageTimestamp)}
                        </Label.Medium.N50>
                    </div>
                )}
                {tabSelectionVisible && (
                    <div className="chat-reply__tabs">
                        <div
                            className={classNames("chat-reply__tab", {
                                selected: messageType === "message",
                            })}
                            onClick={() => {
                                if (isSending) return;
                                setMessageType("message");
                                setShowAiSummary(false);
                            }}
                        >
                            {replyTabText}
                        </div>
                        <div
                            className={classNames("chat-reply__tab", {
                                selected: messageType === "internal_note",
                            })}
                            onClick={() => {
                                if (isSending) return;
                                setMessageType("internal_note");
                                setShowAiSummary(false);
                            }}
                        >
                            Internal note
                        </div>
                        {aiSummaryButtonEnabled && conversation && currentAgent && (
                            <div
                                className={classNames("chat-reply__tab", {
                                    selected: showAiSummary,
                                })}
                            >
                                <Button
                                    showLoadingSpinner={isGeneratingAiSummary}
                                    onClick={() => void handleGenerateAiSummary()}
                                >
                                    <Icon.WandStars.Small.P10 />
                                    Summarise
                                </Button>
                            </div>
                        )}
                    </div>
                )}
                <MessageAttachmentPreviews
                    currentMember={member}
                    wrap
                    articles={articles}
                    removeArticle={
                        removeArticle
                            ? (article) => {
                                  if (shouldManageArticleHyperlinks) {
                                      editorRef.current?.deleteLink(
                                          article.content.title,
                                      );
                                  }
                                  removeArticle(article);
                              }
                            : undefined
                    }
                    articlesV2={articlesV2}
                    removeArticleV2={removeArticleV2}
                    attachments={attachments}
                    removeAttachment={removeAttachment}
                    bookings={bookingsInMessage}
                    removeBooking={removeBooking}
                    request={request?.id || undefined}
                />
                <div className="chat-reply__input">
                    <QuillEditor
                        className="chat-reply__input__editor"
                        placeholder={placeHolderText}
                        initialMessage={replyText}
                        initialMessageHtml={replyHtml}
                        ref={editorRef}
                        onChange={(text: string, html: string) => {
                            const trimmed = text.trim();
                            setReplyText(trimmed);
                            setReplyHtml(html);
                        }}
                        onSend={() => {
                            activeButtonRef.current?.click();
                        }}
                        onLinkEditorClose={() => {
                            setLinkEditorActive(false);
                        }}
                        disabled={isSending}
                    />
                </div>
            </div>
            <div className="chat-reply__actions min-w-0">
                <Flex.Row gap="medium" className="w-auto min-w-0 flex-1">
                    {action === "send" && (
                        <>
                            <div
                                className={classNames(
                                    "chat-reply__action",
                                    linkEditorActive && "disabled",
                                    "web-lib__tooltip web-lib__tooltip--top-left",
                                )}
                                onClick={() =>
                                    !linkEditorActive &&
                                    uploadAttachmentsRef.current?.click()
                                }
                                data-tooltip="Add attachments"
                            >
                                <Icon.AttachFile.Medium />
                            </div>
                            {attachAction && (
                                <div
                                    className="chat-reply__action web-lib__tooltip web-lib__tooltip--top-left"
                                    data-tooltip="Add articles"
                                >
                                    <Button.Ghost
                                        onClick={() => {
                                            attachAction();
                                        }}
                                    >
                                        <Icon.ImportContacts.Medium.N10 />
                                    </Button.Ghost>
                                </div>
                            )}
                        </>
                    )}
                    <div
                        className={classNames(
                            "chat-reply__action",
                            linkEditorActive && "disabled",
                            "web-lib__tooltip web-lib__tooltip--top",
                        )}
                        onClick={() => {
                            if (linkEditorActive) return;
                            setLinkEditorActive(true);
                            editorRef.current?.addLink();
                        }}
                        data-tooltip="Add link"
                    >
                        <Icon.Link.Medium
                            style={{
                                transform: "scale(1.075)",
                                marginLeft: "3px",
                            }}
                        />
                    </div>
                    <input
                        type="file"
                        id="file"
                        ref={uploadAttachmentsRef}
                        style={{ display: "none" }}
                        onChange={(event) => {
                            addFilesToAttachments(event.target.files);
                        }}
                        multiple
                    />
                    {messageType === "internal_note" && (
                        <Mentions
                            agents={agents || []}
                            disabled={linkEditorActive}
                            selectedAgents={mentionedAgents}
                            dueDate={dueDate}
                            onSelect={(selectedAgents, selectedDueDate) => {
                                setMentionedAgents(selectedAgents);
                                setDueDate(selectedDueDate);
                            }}
                        />
                    )}
                </Flex.Row>
                {action === "edit" ? (
                    <div className="ml-3 flex shrink-0 items-center justify-end gap-2">
                        <CharacterLimitIndicator
                            text={replyText}
                            messageType={messageType}
                        />
                        <Button.N90
                            ref={activeSecondaryButtonRef}
                            disabled={linkEditorActive}
                            onClick={closeAction}
                        >
                            Cancel
                        </Button.N90>
                        <Button.P50
                            ref={activeButtonRef}
                            disabled={linkEditorActive}
                            onClick={sendMessage}
                            showLoadingSpinner={isSending}
                        >
                            Save
                        </Button.P50>
                    </div>
                ) : messageType === "message" &&
                  request &&
                  request?.status === "draft" ? (
                    <div className="ml-3 flex shrink-0 flex-nowrap items-center gap-2">
                        <CharacterLimitIndicator
                            text={replyText}
                            messageType={messageType}
                        />
                        <Tooltip>
                            <TooltipTrigger>
                                <Button.N90 disabled>
                                    <div className="flex flex-nowrap items-center gap-1">
                                        <Label.Medium.SemiBold nowrap>
                                            Send
                                        </Label.Medium.SemiBold>
                                        <Icon.Send.Small />
                                    </div>
                                </Button.N90>
                            </TooltipTrigger>
                            <TooltipContent>
                                Please mark the request as 'open' first to send a
                                message.
                            </TooltipContent>
                        </Tooltip>
                    </div>
                ) : (
                    <div className="ml-3 flex shrink-0 flex-nowrap items-center gap-2">
                        <CharacterLimitIndicator
                            text={replyText}
                            messageType={messageType}
                        />
                        <Button
                            ref={activeButtonRef}
                            disabled={
                                !replyText?.trim() || isSending || linkEditorActive
                            }
                            onClick={sendMessage}
                            showLoadingSpinner={isSending}
                            className="whitespace-nowrap"
                        >
                            <div className="flex flex-nowrap items-center gap-1">
                                {messageType === "message" ? (
                                    <>
                                        <Label.Medium.SemiBold nowrap>
                                            Send
                                        </Label.Medium.SemiBold>
                                        <Icon.Send.Small />
                                    </>
                                ) : (
                                    <Label.Medium.SemiBold nowrap>
                                        Add note
                                    </Label.Medium.SemiBold>
                                )}
                            </div>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function CharacterLimitIndicator({
    text,
    messageType,
}: {
    text: string | undefined;
    messageType: MessageType;
}) {
    const replyCharacterCount = (text ?? "").length;

    const shouldShowCharacterCount =
        messageType === "message" && replyCharacterCount > 0;

    if (!shouldShowCharacterCount) {
        return null;
    }

    const isOverCharacterLimit = replyCharacterCount > MESSAGE_MAX_CHARACTER_COUNT;

    const isMessageOverCharacterLimit =
        messageType === "message" && isOverCharacterLimit;

    if (isMessageOverCharacterLimit) {
        return (
            <Tooltip>
                <TooltipTrigger className="w-fit">
                    <Badge variant="amber" className="tabular-nums">
                        <Info className="size-3" />
                        {replyCharacterCount}/{MESSAGE_MAX_CHARACTER_COUNT}
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>{MESSAGE_OVER_LIMIT_TOOLTIP_TEXT}</TooltipContent>
            </Tooltip>
        );
    }

    return (
        <Badge variant="secondary" className="bg-transparent tabular-nums">
            {replyCharacterCount}/{MESSAGE_MAX_CHARACTER_COUNT}
        </Badge>
    );
}

export default MessageInput;
